<script setup lang="ts">
/**
 * MediaBrowser.vue — Media Manager (mmMode) asset browser.
 * Mirrors the v6 webbuild assets.html layout with:
 *   - Folder sidebar (projects & groups tree)
 *   - Toolbar: Action, Upload, Publish, Export buttons
 *   - Quick Upload panel (dropzone + form)
 *   - MediaList grid/list view
 */
import { reactive, computed, ref, watch, onMounted, Ref, inject } from 'vue'
import { useRouter } from 'vue-router'
import { cn } from '@/lib/utils'
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'

import {
	ChevronDown, ChevronUp, PanelLeftClose, PanelLeftOpen, LayoutGrid, List,
	Upload, ArrowUpFromLine, FileOutput, Share2, Check, X, Trash2,
	RefreshCw, CloudUpload, Link, Rss, FolderOpen, Search, Loader2,
	Paintbrush, File as FileIcon, XCircle, CheckCircle2, Download, Send
} from 'lucide-vue-next'

import { useCookies } from '@vueuse/integrations/useCookies'

import MediaList from '@/components/MediaList.vue'
import TreeView from '@/components/TreeView.vue'
import MediaSearchField from '@/components/MediaSearchField.vue'
import Uploader from '@/views/Uploader.vue'
import WidenImport from '@/components/WidenImport.vue'

import { Member } from '@/types/Member'
import permissions from "@/services/permissions"
import { Filters, defaultFilters } from "@/types/Filters"
import { useGroups } from '@/services/Groups'
import { useMedia } from '@/services/Media'
import vhostService from '@/services/Vhost'
import { Group } from "@/types/Groups"
import { Media, defaultMedia } from "@/types/Media"
import { TransferDestination } from "@/types/Transfer"
import { toast } from 'vue-sonner'

interface Tab {
	id: string;
	title: string;
	settings: { filters: { moderationStatus: string } };
	disabled: number;
}

const props = defineProps<{ filters: Filters }>();
const router = useRouter();
const { checkPermission } = permissions();
const { getGroupTree, groupTree } = useGroups();
const { updateFile: gqlUpdateFile } = useMedia();
const { getTabs: gqlGetTabs } = vhostService();
const cookies = useCookies(['groupMenu', 'newSparkMobileView']);

const tab = ref("-1"); // Default "All" for MM mode
const filters = reactive<Filters>({});
const selectedMedia = ref<Media[]>([]);
const mediaView = ref('grid');
const showDialog = ref(false);
const showGroups = ref(false);
const showMediaList = ref(false);
const isDragging = ref(false);
const queryTerm = ref('');
const refresh = ref<number>(0);
const sheetMode = ref('directupload');
const dialogTitle = ref("Error");
const dialogMessage = ref("Error");
const currentCount = ref(0);
const totalCount = ref(0);
const sortOrder = ref('upload DESC');
const loading = ref(true);

const customTabs = ref<Tab[]>([]);

// ──────────────────────────────────────────────
// Quick Upload (v6-style inline upload panel)
// ──────────────────────────────────────────────
const uploadMode = ref(false);
const inlineDropActive = ref(false);

// Upload form state (v6 pattern: Title, Description, Folder, Parent ID)
const uploadForm = reactive({
	title: '',
	message: '',
	parentid: '',
	groupid: '' as string,
})

interface UploadQueueItem {
	file: File
	name: string
	size: number
	progress: number
	status: 'pending' | 'uploading' | 'completed' | 'error'
	error?: string
}
const uploadQueue = ref<UploadQueueItem[]>([])
const isUploading = ref(false)

const handleInlineDrop = (e: DragEvent) => {
	inlineDropActive.value = false
	const files = e.dataTransfer?.files
	if (!files || files.length === 0) return
	addFilesToQueue(Array.from(files))
}

const handleInlineFileInput = (e: Event) => {
	const target = e.target as HTMLInputElement
	if (target.files?.length) {
		addFilesToQueue(Array.from(target.files))
		target.value = ''
	}
}

const addFilesToQueue = (files: File[]) => {
	for (const file of files) {
		uploadQueue.value.push({
			file,
			name: file.name,
			size: file.size,
			progress: 0,
			status: 'pending',
		})
	}
	if (!isUploading.value) processUploadQueue()
}

const removeFromQueue = (index: number) => {
	if (uploadQueue.value[index].status !== 'uploading') {
		uploadQueue.value.splice(index, 1)
	}
}

const cancelUploads = () => {
	uploadMode.value = false
	uploadQueue.value = []
	uploadForm.title = ''
	uploadForm.message = ''
	uploadForm.parentid = ''
	uploadForm.groupid = ''
}

const processUploadQueue = async () => {
	isUploading.value = true
	for (const item of uploadQueue.value) {
		if (item.status !== 'pending') continue
		item.status = 'uploading'
		try {
			const fileData: Record<string, any> = {
				filename: item.file.name,
				extension: item.file.name.substring(item.file.name.lastIndexOf('.') + 1),
				contenttype: item.file.type,
				title: uploadForm.title || item.file.name.replace(/\.[^/.]+$/, ''),
				message: uploadForm.message || '',
				injector: 'uv3upload',
			}
			if (uploadForm.groupid) fileData.groupid = [uploadForm.groupid]
			if (uploadForm.parentid) fileData.parentid = uploadForm.parentid

			const res = await fetch('/services/json', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					method: 'media.getUploadUrl',
					vhost: vhostId?.value,
					fileData,
					v2: 1,
				}),
			})
			const json = await res.json()

			if (json.status) {
				const xhr = new XMLHttpRequest()
				await new Promise<void>((resolve, reject) => {
					xhr.upload.addEventListener('progress', (evt) => {
						if (evt.lengthComputable) {
							item.progress = Math.round((evt.loaded / evt.total) * 100)
						}
					})
					xhr.addEventListener('load', () => {
						if (xhr.status === 200) resolve()
						else reject(new Error('Upload PUT failed'))
					})
					xhr.addEventListener('error', () => reject(new Error('Upload network error')))
					xhr.open('PUT', json.result.url)
					xhr.setRequestHeader('Content-Type', item.file.type)
					xhr.send(item.file)
				})

				await fetch('/services/json', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ method: 'media.uploadCompleted', id: json.result.id }),
				})
				item.status = 'completed'
				item.progress = 100
			} else {
				throw new Error('getUploadUrl failed')
			}
		} catch (err) {
			item.status = 'error'
			item.error = err instanceof Error ? err.message : 'Upload failed'
		}
	}
	isUploading.value = false
	const allDone = uploadQueue.value.every(i => i.status === 'completed' || i.status === 'error')
	if (allDone && uploadQueue.value.some(i => i.status === 'completed')) {
		refresh.value++
		toast.success(`${uploadQueue.value.filter(i => i.status === 'completed').length} file(s) uploaded`)
	}
}

const clearCompletedUploads = () => {
	uploadQueue.value = uploadQueue.value.filter(i => i.status !== 'completed' && i.status !== 'error')
}

const prettySize = (bytes: number): string => {
	if (bytes < 1024) return bytes + ' B'
	if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
	if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB'
	return (bytes / 1073741824).toFixed(1) + ' GB'
}

// ──────────────────────────────────────────────
// Core page state
// ──────────────────────────────────────────────
const userInfo = inject<Ref<Member> | null>('userInfo', null);
const transferList = inject<Ref<TransferDestination[]> | []>('transferList', []);
const vhostId = inject<Ref<string> | null>('vhostId', null);

const formatLargeNumber = (num: number): string => num.toLocaleString('en-US');

const applyFilters = (setFilterCookie: boolean) => {
	if (setFilterCookie) setCookies();
}

const setCookies = () => {
	if (vhostId && vhostId.value) {
		if (filters) {
			cookies.set('mmFilters' + vhostId.value, JSON.stringify(filters), { maxAge: 86400 * 7, path: "/" });
		}
		cookies.set('mmSort' + vhostId.value, sortOrder.value, { maxAge: 86400 * 7, path: "/" });
	}
};

const mergedFilters = computed(() => ({ ...defaultFilters, ...filters }));

const fetchGroupTree = async (vhost: string) => {
	if (vhost) await getGroupTree(vhost);
};

const getCustomTabs = async (userId: string, vhost: string): Promise<void> => {
	// MM mode defaults: All and Archived
	const defaultTabs: Tab[] = [
		{ id: "-1", title: "All", settings: { filters: { moderationStatus: "-1" } }, disabled: 0 },
		{ id: "100", title: "Archived", settings: { filters: { moderationStatus: "100" } }, disabled: 0 }
	];

	let tabsResult: Tab[] = [];
	try {
		tabsResult = await gqlGetTabs(Number(vhost));
	} catch (error) {
		// getTabs may not be available — use defaults
	}

	customTabs.value = [...defaultTabs, ...tabsResult];
};

const selectedGroup = ref<number | null>(null);

watch(() => props.filters, (newFilters) => { Object.assign(filters, { ...newFilters }); }, { deep: true });
watch(mediaView, (newView) => {
	if (vhostId?.value) {
		cookies.set('mmMediaView' + vhostId.value, newView, { maxAge: 86400 * 30, path: '/' });
	}
});
watch(filters, () => {
	if (vhostId && vhostId.value) {
		cookies.set('mmFilters' + vhostId.value, JSON.stringify(filters), { maxAge: 86400 * 7, path: "/" });
	}
}, { deep: true });
watch(tab, (newTabIndex) => {
	const newTab = customTabs.value.find(item => item.id == newTabIndex);
	if (newTab?.settings?.filters) Object.assign(filters, { ...newTab.settings.filters });
});
watch(sortOrder, () => {
	if (vhostId && vhostId.value) {
		cookies.set('mmSort' + vhostId.value, sortOrder.value, { maxAge: 86400 * 7, path: "/" });
	}
});

const setGroupFilter = (id: any) => {
	if (id && id > 0) {
		selectedGroup.value = id;
		filters.groupid = id;
	} else {
		selectedGroup.value = 0;
		delete filters.groupid
	}
};
const setTotalCount = (count: number) => { totalCount.value = count; }
const setCurrentCount = (count: number) => { currentCount.value = count; }
const searchSelect = (mediaItem: Media) => { router.push('/media/' + mediaItem.id); }
const setDragging = (dragging: any) => { isDragging.value = dragging; }

const toggleGroupList = () => {
	showGroups.value = !showGroups.value;
	cookies.set('mmGroupMenu', showGroups.value, { maxAge: 86400 * 30, path: '/' });
};

// Bulk actions
const trashSelected = async () => {
	if (selectedMedia.value.length === 0) return
	const { trashFile } = useMedia()
	try {
		for (const media of selectedMedia.value) {
			await trashFile(media.id)
		}
		toast.success(`Archived ${selectedMedia.value.length} items`)
		selectedMedia.value = []
		refresh.value++
	} catch (err) {
		toast.error('Failed to archive items')
	}
}

const downloadSelected = () => {
	for (const media of selectedMedia.value) {
		if (media.publicUrl) {
			window.open(media.publicUrl, '_blank')
		}
	}
}

// Groups list for upload form
const groups = ref<{ id: string; name: string }[]>([])
const fetchGroups = async () => {
	if (!vhostId?.value) return
	try {
		const { getGroups } = useGroups()
		const result = await getGroups(Number(vhostId.value))
		groups.value = result.map((g: any) => ({ id: String(g.id), name: g.name }))
	} catch (err) {
		console.error('Error fetching groups:', err)
	}
}

const initPage = async () => {
	if (!vhostId?.value) return;
	sortOrder.value = 'upload DESC';
	fetchGroupTree(vhostId.value);
	fetchGroups();

	if (userInfo?.value) {
		await getCustomTabs(userInfo.value.id, vhostId.value);
	} else {
		// Set default tabs immediately even without userInfo — this is always mmMode
		customTabs.value = [
			{ id: "-1", title: "All", settings: { filters: { moderationStatus: "-1" } }, disabled: 0 },
			{ id: "100", title: "Archived", settings: { filters: { moderationStatus: "100" } }, disabled: 0 }
		];
	}

	sortOrder.value = cookies.get('mmSort' + vhostId.value) || 'upload DESC';
	const savedView = cookies.get('mmMediaView' + vhostId.value);
	if (savedView === 'grid' || savedView === 'list') mediaView.value = savedView;

	const tmpFilters = cookies.get('mmFilters' + vhostId.value);
	Object.assign(filters, { ...structuredClone(defaultFilters), ...tmpFilters });
	if (filters.groupid) selectedGroup.value = filters.groupid;

	if (filters.moderationStatus) {
		const matchingTab = customTabs.value.find(item => item.settings?.filters?.moderationStatus == filters.moderationStatus);
		if (matchingTab) tab.value = matchingTab.id;
	} else {
		tab.value = "-1";
	}
	const savedGroupMenu = cookies.get('mmGroupMenu');
	showGroups.value = (savedGroupMenu !== undefined && savedGroupMenu !== null) ? savedGroupMenu : true;
	showMediaList.value = true;
};

onMounted(() => { initPage(); });

// If userInfo wasn't ready at mount, retry when it becomes available
watch(() => userInfo?.value, async (newVal) => {
	if (newVal && vhostId?.value && customTabs.value.length <= 2) {
		await getCustomTabs(newVal.id, vhostId.value);
	}
}, { immediate: false });
</script>

<template>
	<div v-if="userInfo && vhostId && userInfo.permissions" class="flex border-t text-secondary-foreground h-screen">

		<!-- ══════════ Folder Sidebar (v6 Projects & Folders) ══════════ -->
		<div v-if="(showGroups || isDragging)" class="hidden md:flex border-r w-[240px] shrink-0 flex-col bg-background">
			<div class="flex items-center justify-between px-3 py-2.5 border-b">
				<span class="text-sm font-medium">Projects & Folders</span>
				<button @click="toggleGroupList()" class="p-1 rounded hover:bg-muted text-muted-foreground">
					<PanelLeftClose class="h-4 w-4" />
				</button>
			</div>
			<div class="flex-1 overflow-y-auto p-2">
				<button class="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted transition-colors mb-1"
					:class="{ 'bg-muted font-medium': !selectedGroup }"
					@click="setGroupFilter(0)">
					All Assets
				</button>
				<TreeView v-if="groupTree" :groupTree="groupTree" :selectedGroup="selectedGroup || undefined" @select-group="setGroupFilter" />
			</div>
		</div>

		<!-- ══════════ Main Content ══════════ -->
		<div class="flex-1 flex flex-col min-w-0">

			<!-- Tab bar (All / Archived / Custom) -->
			<div class="border-b bg-background">
				<div class="flex items-center gap-1 px-4 pt-1">
					<button v-if="!showGroups" @click="toggleGroupList()" class="p-1.5 rounded hover:bg-muted text-muted-foreground mr-1" title="Show folders">
						<PanelLeftOpen class="h-4 w-4" />
					</button>
					<button v-for="t in customTabs" :key="t.id"
						class="px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap rounded-t-md border border-b-0"
						:class="tab === t.id ? 'bg-card text-foreground border-border -mb-px z-10' : 'bg-transparent text-muted-foreground hover:text-foreground border-transparent hover:bg-muted/50'"
						@click="tab = t.id"
						:disabled="t.disabled === 1">
						{{ t.title }}
					</button>
				</div>
			</div>

			<!-- ══════════ Toolbar (v6 Action / Upload / Publish / Export row) ══════════ -->
			<div class="border-b bg-muted/30 px-4 py-2">
				<div class="flex items-center justify-between flex-wrap gap-2">
					<!-- Left: action buttons -->
					<div class="flex items-center gap-2 flex-wrap">
						<!-- Action dropdown -->
						<DropdownMenu>
							<DropdownMenuTrigger as-child>
								<Button variant="default" size="sm" class="gap-1.5 text-xs" :disabled="selectedMedia.length === 0">
									<Paintbrush class="h-3.5 w-3.5" /> Action <ChevronDown class="h-3 w-3" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuItem class="text-xs gap-2" @click="downloadSelected"><Download class="h-3.5 w-3.5" /> Download</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2"><Share2 class="h-3.5 w-3.5" /> Share for Review</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2"><Link class="h-3.5 w-3.5" /> Share Asset Link</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem class="text-xs gap-2" @click="trashSelected"><Trash2 class="h-3.5 w-3.5" /> Archive</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2"><RefreshCw class="h-3.5 w-3.5" /> Re-transcode</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						<!-- Upload dropdown (v6: Direct Upload, Share Uploader) -->
						<DropdownMenu>
							<DropdownMenuTrigger as-child>
								<Button variant="outline" size="sm" class="gap-1.5 text-xs">
									<Upload class="h-3.5 w-3.5" /> Upload <ChevronDown class="h-3 w-3" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuItem class="text-xs gap-2" @click="uploadMode = true">
									<CloudUpload class="h-3.5 w-3.5" /> Direct Upload
								</DropdownMenuItem>
								<DropdownMenuItem class="text-xs gap-2">
									<Link class="h-3.5 w-3.5" /> Share Uploader
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						<!-- Publish -->
						<DropdownMenu>
							<DropdownMenuTrigger as-child>
								<Button variant="outline" size="sm" class="gap-1.5 text-xs" :disabled="selectedMedia.length === 0">
									<ArrowUpFromLine class="h-3.5 w-3.5" /> Publish <ChevronDown class="h-3 w-3" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuLabel class="text-xs">Transfer Destinations</DropdownMenuLabel>
								<DropdownMenuSeparator />
								<DropdownMenuItem class="text-xs gap-2" @click="router.push('/transfers')">
									<Send class="h-3.5 w-3.5" /> Manage Destinations
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>

						<!-- Export -->
						<Button variant="outline" size="sm" class="gap-1.5 text-xs" :disabled="selectedMedia.length === 0" @click="downloadSelected">
							<FileOutput class="h-3.5 w-3.5" /> Export
						</Button>

						<!-- Selection count -->
						<span v-if="selectedMedia.length > 0" class="text-xs text-muted-foreground ml-1 tabular-nums">
							{{ selectedMedia.length }} selected
						</span>
					</div>

					<!-- Right: count, sort, view -->
					<div class="flex items-center gap-3">
						<span v-if="currentCount > 0 && totalCount > 0" class="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
							{{ currentCount }} of {{ formatLargeNumber(totalCount) }} Result(s)
						</span>

						<Select v-model="sortOrder">
							<SelectTrigger class="h-8 w-40 text-xs bg-card">
								<SelectValue placeholder="Sort order" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value="title ASC">Name (A - Z)</SelectItem>
									<SelectItem value="title DESC">Name (Z - A)</SelectItem>
									<SelectItem value="upload DESC">Most Recent</SelectItem>
									<SelectItem value="upload ASC">Oldest First</SelectItem>
									<SelectItem value="hits DESC">Most views</SelectItem>
									<SelectItem value="filesize DESC">Largest</SelectItem>
									<SelectItem value="filesize ASC">Smallest</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>

						<div class="flex items-center border rounded-md overflow-hidden">
							<button class="p-1.5 transition-colors" :class="mediaView === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'" @click="mediaView = 'grid'">
								<LayoutGrid class="h-4 w-4" />
							</button>
							<button class="p-1.5 transition-colors" :class="mediaView === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'" @click="mediaView = 'list'">
								<List class="h-4 w-4" />
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- ══════════ Quick Upload Panel (v6 pattern) ══════════ -->
			<div v-if="uploadMode" class="border-b bg-background px-4 py-4">
				<div class="flex gap-4">
					<!-- Left: Dropzone -->
					<div class="flex-1 flex flex-col gap-3">
						<!-- Drop area -->
						<div
							class="border-2 border-dashed rounded-lg transition-colors relative flex-1 min-h-[140px]"
							:class="inlineDropActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 bg-muted/20'"
							@dragover.prevent="inlineDropActive = true"
							@dragleave.prevent="inlineDropActive = false"
							@drop.prevent="handleInlineDrop"
						>
							<input
								type="file"
								multiple
								class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
								@change="handleInlineFileInput"
							/>
							<div class="flex flex-col items-center justify-center h-full py-6 gap-1.5">
								<CloudUpload class="h-10 w-10 text-muted-foreground/40" />
								<h3 class="text-sm font-medium text-muted-foreground">Drag and drop to add files</h3>
								<p class="text-xs text-muted-foreground/70">...or click to select a file from your computer</p>
							</div>
						</div>

						<!-- Upload queue / file list -->
						<div v-if="uploadQueue.length > 0" class="space-y-1">
							<div v-for="(item, idx) in uploadQueue" :key="idx" class="flex items-center gap-2 text-xs bg-muted/40 border rounded px-2.5 py-1.5">
								<FileIcon class="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
								<span class="truncate flex-1">{{ item.name }}</span>
								<span class="text-muted-foreground shrink-0">{{ prettySize(item.size) }}</span>
								<div v-if="item.status === 'uploading'" class="w-20 h-1.5 bg-muted rounded-full overflow-hidden shrink-0">
									<div class="h-full bg-primary transition-all rounded-full" :style="{ width: item.progress + '%' }"></div>
								</div>
								<span v-if="item.status === 'uploading'" class="text-muted-foreground shrink-0 tabular-nums w-8 text-right">{{ item.progress }}%</span>
								<CheckCircle2 v-else-if="item.status === 'completed'" class="h-3.5 w-3.5 text-green-500 shrink-0" />
								<XCircle v-else-if="item.status === 'error'" class="h-3.5 w-3.5 text-destructive shrink-0 cursor-pointer" :title="item.error" @click="removeFromQueue(idx)" />
								<button v-else class="text-muted-foreground hover:text-destructive shrink-0" @click="removeFromQueue(idx)">
									<X class="h-3 w-3" />
								</button>
							</div>
						</div>
					</div>

					<!-- Right: Upload form fields (v6 pattern: Title, Description, Folder, Parent ID) -->
					<div class="w-[320px] shrink-0 flex flex-col gap-3">
						<div class="flex justify-end gap-2">
							<Button variant="outline" size="sm" class="text-xs" @click="cancelUploads">Cancel</Button>
							<Button size="sm" class="text-xs" @click="processUploadQueue"
								:disabled="uploadQueue.length === 0 || isUploading || !uploadForm.title">
								{{ isUploading ? 'Uploading...' : 'Upload File(s)' }}
							</Button>
						</div>

						<div class="space-y-1">
							<Label class="text-xs">Title</Label>
							<Input v-model="uploadForm.title" placeholder="Enter title here" class="h-8 text-xs" />
						</div>

						<div class="space-y-1">
							<Label class="text-xs">Description</Label>
							<Textarea v-model="uploadForm.message" placeholder="Enter description" rows="3" class="text-xs resize-none" />
						</div>

						<div class="space-y-1">
							<Label class="text-xs">Folder</Label>
							<Select v-model="uploadForm.groupid">
								<SelectTrigger class="h-8 text-xs">
									<SelectValue placeholder="Select Folder" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem v-for="group in groups" :key="group.id" :value="group.id">
										{{ group.name }}
									</SelectItem>
								</SelectContent>
							</Select>
						</div>

						<div class="space-y-1">
							<Label class="text-xs">Parent ID</Label>
							<Input v-model="uploadForm.parentid" placeholder="Enter parent ID here" class="h-8 text-xs" />
						</div>

						<!-- Upload progress (v6 pattern) -->
						<div v-if="isUploading" class="space-y-1">
							<div class="flex items-center justify-between text-xs text-muted-foreground">
								<span>Uploading...</span>
								<span>{{ uploadQueue.filter(i => i.status === 'completed').length }} / {{ uploadQueue.length }} complete</span>
							</div>
							<div class="w-full h-1.5 bg-muted rounded-full overflow-hidden">
								<div class="h-full bg-primary transition-all rounded-full"
									:style="{ width: Math.round((uploadQueue.filter(i => i.status === 'completed').length / uploadQueue.length) * 100) + '%' }">
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- ══════════ Media Grid/List ══════════ -->
			<div class="flex-1 overflow-y-auto p-4">
				<MediaList v-if="vhostId !== null && showMediaList"
					v-model="selectedMedia"
					:filters="filters"
					:type="mediaView"
					:vhostId="vhostId"
					:sort-order="sortOrder"
					@update-dragging="setDragging"
					@set-total-count="setTotalCount"
					@set-current-count="setCurrentCount"
					default-profile="11"
					:refreshKey="refresh" />
			</div>
		</div>
	</div>

	<!-- Error dialog -->
	<AlertDialog v-model:open="showDialog">
		<AlertDialogContent class="sm:max-w-md">
			<AlertDialogHeader>
				<AlertDialogTitle>{{ dialogTitle }}</AlertDialogTitle>
				<AlertDialogDescription>{{ dialogMessage }}</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel @click="showDialog = false">Close</AlertDialogCancel>
			</AlertDialogFooter>
		</AlertDialogContent>
	</AlertDialog>
</template>
